import type { ButtonHTMLAttributes, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger-outline';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  fullWidth?: boolean;
  /** Aktion ist abgeschickt, aber noch nicht bestätigt. Sperrt und zeigt Punkte. */
  pending?: boolean;
  children: ReactNode;
}

// `transform` gehört mit in die Transition: sonst schnappt `active:scale` hart
// ein und wieder heraus, und der Druckpunkt klackt statt nachzugeben.
const base =
  'relative inline-flex items-center justify-center gap-2 rounded-2xl font-display font-semibold ' +
  'transition-[color,background-color,border-color,transform] duration-150 ' +
  'active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none select-none';

const sizing = 'min-h-14 px-6 text-base';

const variants: Record<Variant, string> = {
  primary: 'bg-accent text-accent-fg hover:brightness-110',
  secondary:
    'bg-surface-2 text-fg border border-surface-border hover:border-accent/60',
  ghost: 'bg-transparent text-fg-muted hover:text-fg hover:bg-surface',
  // Fehler/Gefahr werden bewusst NICHT rot gefüllt — Rot bleibt der Mafia
  // vorbehalten. Ein Outline-Stil markiert "Vorsicht", ohne die Farbsprache
  // der Fraktionen zu verwässern (GDD §33).
  'danger-outline': 'bg-transparent text-fg border-2 border-fg-faint hover:border-fg-muted',
};

export function Button({
  variant = 'primary',
  fullWidth = false,
  pending = false,
  disabled,
  className = '',
  children,
  ...rest
}: ButtonProps) {
  return (
    <button
      className={`${base} ${sizing} ${variants[variant]} ${fullWidth ? 'w-full' : ''} ${className}`}
      disabled={disabled || pending}
      aria-busy={pending || undefined}
      {...rest}
    >
      {/* Label bleibt im Layout stehen, damit der Button beim Absenden nicht
          seine Breite ändert — sonst springt der Footer unter dem Daumen weg. */}
      <span className={pending ? 'invisible' : undefined}>{children}</span>
      {pending && <PendingDots />}
    </button>
  );
}

function PendingDots() {
  return (
    <span className="absolute flex items-center gap-1" aria-hidden="true">
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          className="h-1.5 w-1.5 rounded-full bg-current opacity-40 animate-pulse-soft"
          style={{ animationDelay: `${index * 0.16}s` }}
        />
      ))}
    </span>
  );
}
